import { Cluster } from '@cast/types';
import {
  KentFeature,
  InstallableKentFeature,
  KnownKentFeature,
  KentEC2NodeClass,
  KentKentrollerCondition,
  KentKentrollerConditionStatus,
  KentKentrollerConditionType,
  KentKentrollerStatusDetails,
} from '@cast/types-kent';
import { DerivedKentrollerStatus } from '../types';

/** Accepts all known Kent features, not just installable ones, because some features are installed as part of other features. */
export const isKentFeatureInstalled = (feature: KnownKentFeature): boolean => {
  return feature.status !== 'UNAVAILABLE';
};

export const isKentFeatureDisabled = (feature: KnownKentFeature): boolean => {
  return feature.status === 'DISABLED';
};

export const isKentFeatureEnabled = (feature: KnownKentFeature): boolean => {
  return feature.status === 'ENABLED';
};

export const filterInstallableKentFeatures = (
  features: KentFeature[]
): InstallableKentFeature[] => {
  return features.filter(
    (feature) =>
      feature.type !== 'FEATURE_TYPE_UNSPECIFIED' &&
      feature.type !== 'CONTINUOUS_REBALANCING'
  );
};

export const isAtLeastOneKentFeatureInstalled = (
  features: KentFeature[]
): boolean => {
  const installableFeatures = filterInstallableKentFeatures(features);
  return installableFeatures.some(isKentFeatureInstalled);
};

export const isKentEligibilityDataAvailable = (cluster: Cluster): boolean => {
  // There is no information about KENT eligibility until the cluster's snapshot is received
  return Boolean(cluster.agentSnapshotReceivedAt);
};

export const isNodeClassUsingCapacityReservations = (
  nodeClass: KentEC2NodeClass
): boolean =>
  (nodeClass?.spec?.capacityReservationSelectorTerms?.length ?? 0) > 0;

const getKentrollerCondition = ({
  kentrollerStatusDetails,
  conditionType,
}: {
  kentrollerStatusDetails: KentKentrollerStatusDetails;
  conditionType: KentKentrollerConditionType;
}): KentKentrollerCondition | undefined => {
  return kentrollerStatusDetails.conditions?.find(
    (condition) => condition.type === conditionType
  );
};

export const getKentrollerConditionStatus = ({
  kentrollerStatusDetails,
  conditionType,
}: {
  kentrollerStatusDetails: KentKentrollerStatusDetails;
  conditionType: KentKentrollerConditionType;
}): KentKentrollerConditionStatus => {
  const condition = getKentrollerCondition({
    kentrollerStatusDetails,
    conditionType,
  });
  return condition?.status ?? 'KENT_CONDITION_STATUS_UNSPECIFIED';
};

export const deriveKentrollerStatus = (
  statusDetails: KentKentrollerStatusDetails
): DerivedKentrollerStatus => {
  const readyStatus = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENTROLLER_READY',
  });

  if (readyStatus !== 'KENT_CONDITION_STATUS_TRUE') {
    return 'not_ready';
  }

  const versionValidStatus = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENTROLLER_VERSION_VALID',
  });

  if (versionValidStatus !== 'KENT_CONDITION_STATUS_TRUE') {
    return 'warning';
  }

  return 'ready';
};
